import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteNotifications } from '@/hooks/queries/useNotificationQueries';
import { useDropdown } from '@/hooks/useDropdown';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { useAuthStore } from '@/stores/authStore';
import { NotificationDropdownContent } from './NotificationDropdownContent';

type NotificationDropdownProps = {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  onMarkAllAsRead: () => void;
};

export default function NotificationDropdown({
  isOpen,
  onClose,
  onMarkAllAsRead,
  anchorRef,
}: NotificationDropdownProps) {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const position = useDropdownPosition({ isOpen, anchorRef, offset: 8 });

  useDropdown({ isOpen, onClose, anchorRef, dropdownRefs: [dropdownRef] });

  const { data, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteNotifications({
    isAuthenticated,
  });

  const notifications = useMemo(() => data?.pages.flatMap((page) => page.notifications) ?? [], [data]);

  useEffect(() => {
    if (isOpen) refetch();
  }, [isOpen, refetch]);

  const handleItemClick = (poolId: number) => {
    onClose();
    navigate(`/pools/${poolId}`);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="fixed z-50 top-20 left-4 right-4 w-[calc(100%-2rem)] animate-slideDown lg:left-auto lg:top-[var(--dropdown-top)] lg:right-[var(--dropdown-right)] lg:w-[420px]"
      style={
        {
          '--dropdown-top': `${position.top}px`,
          '--dropdown-right': `${position.right}px`,
        } as React.CSSProperties
      }
    >
      <NotificationDropdownContent
        notifications={notifications}
        loading={isLoading}
        onItemClick={handleItemClick}
        onMarkAllAsRead={onMarkAllAsRead}
        onLoadMore={fetchNextPage}
        hasMore={!!hasNextPage}
        isLoadingMore={isFetchingNextPage}
      />
    </div>
  );
}
